import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, MapPin, Package, Pencil, Plus, Trash2, LockKeyhole } from 'lucide-react';
import { useProjects } from '@/hooks/use-projects';
import {
  useCreateGearItem,
  useDeleteGearItem,
  useGearItems,
  useGearUsage,
  useUpdateGearItem,
} from '@/hooks/use-gear-items';
import { useAllCFLocations } from '@/hooks/use-cf-locations';
import { handleApiError } from '@/lib/api-client';
import type { CreateGearItemPayload, GearItem } from '@/lib/types';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { Skeleton } from '@/app/components/ui/skeleton';
import { toast } from '@/app/components/ui/simple-toaster';

const EMPTY_FORM: CreateGearItemPayload = {
  name: '',
  gear_type: '',
  identifier: '',
  current_location: null,
  notes: '',
};

function locationAddress(item: GearItem) {
  const location = item.current_location_display;
  if (!location) return 'Unassigned';
  return [location.address, location.city, location.state_province]
    .filter(Boolean)
    .join(', ') || location.name;
}

export function GearLitePage() {
  const { data: gearData, isLoading } = useGearItems();
  const { data: usage } = useGearUsage();
  const { data: locations } = useAllCFLocations();
  const [projectSearch, setProjectSearch] = useState('');
  const [debouncedProjectSearch, setDebouncedProjectSearch] = useState('');
  const { data: projects, isFetching: isSearchingProjects } = useProjects(
    { search: debouncedProjectSearch, page_size: 10 },
    debouncedProjectSearch.length >= 2,
  );
  const createMutation = useCreateGearItem();
  const updateMutation = useUpdateGearItem();
  const deleteMutation = useDeleteGearItem();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GearItem | null>(null);
  const [form, setForm] = useState<CreateGearItemPayload>(EMPTY_FORM);
  const [locationTouched, setLocationTouched] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProjectSearch(projectSearch.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [projectSearch]);

  const locationOptions = useMemo(() => {
    const byLocationId = new Map<
      number,
      { id: number; name: string; jobNames: string[] }
    >();

    (locations ?? []).forEach((location) => {
      byLocationId.set(location.id, {
        id: location.id,
        name: location.name,
        jobNames: [],
      });
    });

    (projects?.results ?? []).forEach((project) => {
      if (project.location_fk === null) return;
      const existing = byLocationId.get(project.location_fk);
      if (existing) {
        existing.jobNames.push(project.name);
        return;
      }
      byLocationId.set(project.location_fk, {
        id: project.location_fk,
        name: project.name,
        jobNames: [project.name],
      });
    });

    const editingLocation = editingItem?.current_location_display;
    if (editingLocation) {
      const existing = byLocationId.get(editingLocation.id);
      if (existing) {
        existing.name = editingLocation.name;
      } else {
        byLocationId.set(editingLocation.id, {
          id: editingLocation.id,
          name: editingLocation.name,
          jobNames: [],
        });
      }
    }

    const options = [...byLocationId.values()].map((location) => ({
      key: `location-${location.id}`,
      id: location.id,
      label: location.jobNames.length
        ? `${location.jobNames.join(', ')} — ${location.name}`
        : location.name,
      group: location.jobNames.length ? 'jobs' : 'locations',
    }));

    return {
      locations: options.filter((option) => option.group === 'locations'),
      jobs: options.filter((option) => option.group === 'jobs'),
    };
  }, [editingItem, locations, projects]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setLocationTouched(false);
    setProjectSearch('');
    setDialogOpen(true);
  };

  const openEdit = (item: GearItem) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      gear_type: item.gear_type,
      identifier: item.identifier,
      current_location: item.current_location,
      notes: item.notes,
    });
    setLocationTouched(false);
    setProjectSearch('');
    setDialogOpen(true);
  };

  const submit = async () => {
    try {
      if (editingItem) {
        const updateData = { ...form };
        if (!locationTouched) {
          delete updateData.current_location;
        }
        await updateMutation.mutateAsync({ id: editingItem.id, data: updateData });
        toast.success('Gear item updated');
      } else {
        await createMutation.mutateAsync(form);
        toast.success('Gear item added');
      }
      setDialogOpen(false);
    } catch (error) {
      const apiError = handleApiError(error);
      if (apiError.code === 'plan_limit_exceeded') {
        toast.error('Upgrade to H3Gear', {
          description: apiError.message,
          action: {
            label: 'Upgrade to H3Gear',
            onClick: () => { window.location.href = '/settings/subscription'; },
          },
        });
        return;
      }
      toast.error(apiError.message);
    }
  };

  const remove = async (item: GearItem) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try {
      await deleteMutation.mutateAsync(item.id);
      toast.success('Gear item deleted');
    } catch (error) {
      toast.error(handleApiError(error).message);
    }
  };

  const selectedLocationKey = editingItem?.current_location_is_deleted && !locationTouched
    ? ''
    : form.current_location
    ? `location-${form.current_location}`
    : 'none';
  const atCap = usage?.at_cap ?? false;
  const nearCap = usage?.near_cap ?? false;

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 sm:text-3xl">
            <Package className="h-7 w-7" />
            Gear Lite
          </h1>
          <p className="mt-1 text-gray-600">A lightweight gear register for every organization.</p>
        </div>
        <Button onClick={openCreate} disabled={atCap}>
          <Plus className="mr-2 h-4 w-4" />
          Add gear
        </Button>
      </div>

      {atCap && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Gear Lite limit reached</AlertTitle>
          <AlertDescription>
            Your register contains {usage?.limit ?? 200} items.{' '}
            <a href="/settings/subscription" className="font-medium underline">
              Upgrade to H3Gear
            </a>{' '}
            to add more gear.
          </AlertDescription>
        </Alert>
      )}
      {!atCap && nearCap && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Approaching your Gear Lite limit</AlertTitle>
          <AlertDescription>
            You are using {usage?.percentage.toFixed(1)}% of your {usage?.limit ?? 200} items.{' '}
            <a href="/settings/subscription" className="font-medium underline">
              Upgrade to H3Gear
            </a>{' '}
            for a full equipment management workflow.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Register
            {usage && <Badge variant="outline">{usage.count} / {usage.limit}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="space-y-2">
              {[1, 2, 3].map((row) => <Skeleton key={row} className="h-12 w-full" />)}
            </div>
          )}
          {!isLoading && (gearData?.results.length ?? 0) === 0 && (
            <div className="py-10 text-center text-gray-500">
              <Package className="mx-auto mb-3 h-10 w-10 text-gray-400" />
              <p>No gear items yet. Add your first item to get started.</p>
            </div>
          )}
          {!isLoading && (gearData?.results.length ?? 0) > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead>Current location</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gearData?.results.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>{item.gear_type}</TableCell>
                      <TableCell>{item.identifier || '—'}</TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <MapPin className="mt-0.5 h-4 w-4 text-gray-400" />
                          <span>
                            {item.current_location_display?.name ?? 'Unassigned'}
                            <span className="block text-xs text-gray-500">{locationAddress(item)}</span>
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(item)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-dashed bg-gray-50/70 opacity-80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-600">
            <LockKeyhole className="h-5 w-5" />
            Upgrade to H3Gear
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-600">
          H3Gear adds inspections, certifications, service-life tracking, job history,
          QR codes, and a complete audit trail.
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit gear item' : 'Add gear item'}</DialogTitle>
            <DialogDescription>
              Keep this register focused on the gear you have and where it is now.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="gear-name">Name *</Label>
              <Input
                id="gear-name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="e.g. Blue rope 01"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gear-type">Gear type *</Label>
              <Input
                id="gear-type"
                value={form.gear_type}
                onChange={(event) => setForm({ ...form, gear_type: event.target.value })}
                placeholder="e.g. Rope, harness, helmet"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gear-identifier">Identifier (optional)</Label>
              <Input
                id="gear-identifier"
                value={form.identifier}
                onChange={(event) => setForm({ ...form, identifier: event.target.value })}
                placeholder="Serial number or internal tag"
              />
            </div>
            <div className="space-y-2">
              <Label>Current location (optional)</Label>
              <Input
                aria-label="Search jobs"
                placeholder="Search jobs by name or number..."
                value={projectSearch}
                onChange={(event) => setProjectSearch(event.target.value)}
              />
              {isSearchingProjects && (
                <p className="text-xs text-gray-500">Searching jobs...</p>
              )}
              <Select
                value={selectedLocationKey}
                onValueChange={(value) => {
                  const locationId = value === 'none'
                    ? null
                    : Number(value.replace('location-', ''));
                  setLocationTouched(true);
                  setForm({ ...form, current_location: locationId });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Choose a location" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {locationOptions.locations.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Locations</SelectLabel>
                      {locationOptions.locations.map((option) => (
                        <SelectItem key={option.key} value={option.key}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {locationOptions.jobs.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Jobs</SelectLabel>
                      {locationOptions.jobs.map((option) => (
                        <SelectItem key={option.key} value={option.key}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="gear-notes">Notes (optional)</Label>
              <Textarea
                id="gear-notes"
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
                placeholder="Additional context"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save gear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
